<template>
  <v-app>
    <!-- Full-screen routes (no layout) -->
    <RouterView v-if="isFullscreenRoute" />

    <!-- Normal routes (with layout) -->
    <template v-else>
    <v-app-bar
      app
      color="primary"
      dark
      elevation="1"
    >
      <v-app-bar-nav-icon
        @click="drawer = !drawer"
      ></v-app-bar-nav-icon>

      <v-toolbar-title>
        <v-icon left class="mr-2">mdi-tree</v-icon>
        Flora Database Manager
      </v-toolbar-title>

      <v-spacer></v-spacer>

      <div v-if="authStore.isAuthenticated" class="d-flex align-center mr-4">
        <v-btn
          variant="text"
          :to="{ name: 'account' }"
          prepend-icon="mdi-account"
          class="mr-2"
        >
          {{ authStore.username }}
        </v-btn>
        <v-btn
          variant="text"
          @click="handleLogout"
          prepend-icon="mdi-logout"
        >
          Logout
        </v-btn>
      </div>

      <v-menu>
        <template #activator="{ props }">
          <v-btn
            icon
            v-bind="props"
            title="Settings"
          >
            <v-icon>mdi-cog</v-icon>
          </v-btn>
        </template>

        <v-list>
          <v-list-subheader>Theme Settings</v-list-subheader>

          <v-list-item
            @click="themeStore.setTheme('dark')"
            :class="{ 'v-list-item--active': themeStore.isDark }"
          >
            <template #prepend>
              <v-icon>mdi-weather-night</v-icon>
            </template>
            <v-list-item-title>Dark Mode</v-list-item-title>
            <template #append>
              <v-icon v-if="themeStore.isDark" color="primary">mdi-check</v-icon>
            </template>
          </v-list-item>

          <v-list-item
            @click="themeStore.setTheme('light')"
            :class="{ 'v-list-item--active': !themeStore.isDark }"
          >
            <template #prepend>
              <v-icon>mdi-weather-sunny</v-icon>
            </template>
            <v-list-item-title>Light Mode</v-list-item-title>
            <template #append>
              <v-icon v-if="!themeStore.isDark" color="primary">mdi-check</v-icon>
            </template>
          </v-list-item>

          <v-divider />

          <v-list-item>
            <template #prepend>
              <v-icon>mdi-information</v-icon>
            </template>
            <v-list-item-title>About</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>

      <v-btn
        icon
        @click="themeStore.toggleTheme()"
        :title="themeStore.isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'"
      >
        <v-icon>{{ themeStore.isDark ? 'mdi-weather-sunny' : 'mdi-weather-night' }}</v-icon>
      </v-btn>
    </v-app-bar>

    <v-navigation-drawer
      v-model="drawer"
      app
      temporary
    >
      <v-list>
        <v-list-item
          prepend-icon="mdi-view-dashboard"
          title="Dashboard"
          to="/"
        ></v-list-item>

        <v-list-item
          prepend-icon="mdi-account-circle"
          title="My Account"
          to="/account"
        ></v-list-item>

        <v-list-item
          prepend-icon="mdi-flower"
          title="Plants"
          to="/plants"
        ></v-list-item>

        <v-list-item
          prepend-icon="mdi-leaf-maple"
          title="Add Plant with Auto-fill"
          to="/plants/create-new"
        ></v-list-item>

        <v-list-item
          prepend-icon="mdi-sprout"
          title="Cultivars"
          to="/cultivars"
        ></v-list-item>

        <v-list-item
          prepend-icon="mdi-heart"
          title="Annie's Favorites"
          to="/favorites"
        ></v-list-item>

        <v-list-item
          prepend-icon="mdi-currency-usd"
          title="Nursery Pricing"
          to="/nursery-pricing"
        ></v-list-item>

        <v-list-item
          prepend-icon="mdi-upload"
          title="Import Data"
          to="/import"
        ></v-list-item>

        <v-list-item
          prepend-icon="mdi-alert-circle-check"
          title="Needs Attention"
          to="/needs-attention"
        ></v-list-item>

        <v-list-item
          prepend-icon="mdi-database-edit"
          title="Data Workflow"
          to="/data-workflow"
        ></v-list-item>

        <v-list-item
          prepend-icon="mdi-link-variant"
          title="Source URLs"
          to="/source-urls"
        ></v-list-item>

        <v-list-item
          prepend-icon="mdi-table-edit"
          title="Bulk Editor"
          to="/bulk-editor"
        ></v-list-item>

        <v-list-item
          prepend-icon="mdi-file-import"
          title="Bulk Import"
          to="/bulk-import"
        ></v-list-item>

        <v-list-item
          prepend-icon="mdi-web-sync"
          title="Bulk Scraping"
          to="/bulk-scraping"
        ></v-list-item>

        <v-list-item
          prepend-icon="mdi-palette"
          title="Render Colours (batch)"
          to="/render-colors-batch"
        ></v-list-item>

        <v-list-item
          prepend-icon="mdi-scale-balance"
          title="Reconcile Data"
          to="/reconciliation"
        ></v-list-item>

        <v-list-item
          prepend-icon="mdi-image-search"
          title="Image Finder"
          to="/image-finder"
        ></v-list-item>

        <v-list-item
          prepend-icon="mdi-image-multiple"
          title="Image Management"
          to="/images"
        ></v-list-item>

        <v-list-item
          prepend-icon="mdi-vector-square"
          title="Artwork Manager"
          to="/artwork-manager"
        ></v-list-item>

        <v-list-item
          prepend-icon="mdi-delete-forever"
          title="Scheduled Deletions"
          to="/scheduled-deletions"
        ></v-list-item>

        <v-divider></v-divider>

        <v-list-item
          prepend-icon="mdi-floor-plan"
          title="Site Plan Generator"
          to="/site-plan"
        ></v-list-item>

        <v-list-item
          prepend-icon="mdi-map-search"
          title="County GIS Config"
          to="/county-gis-config"
        ></v-list-item>

        <v-list-item
          prepend-icon="mdi-shield-key"
          title="Trusted Origins"
          to="/trusted-origins"
        ></v-list-item>

        <v-list-item
          prepend-icon="mdi-account-multiple"
          title="Users"
          to="/users"
        ></v-list-item>

        <v-list-item
          prepend-icon="mdi-land-rows-horizontal"
          title="Soil Compatibility"
          to="/soil-compatibility"
        ></v-list-item>

        <v-list-item
          prepend-icon="mdi-palette-swatch"
          title="Palette Builder"
          to="/palette-builder"
        ></v-list-item>

        <template v-if="isDev">
          <v-divider></v-divider>
          <v-list-subheader>Developer Tools</v-list-subheader>
          <v-list-item
            prepend-icon="mdi-database-sync"
            title="Sync from Production"
            to="/db-sync"
          ></v-list-item>
        </template>

        <!-- GIS Layer Viewer - hidden for now, re-enable when needed
        <v-list-item
          prepend-icon="mdi-map-legend"
          title="GIS Layer Viewer"
          to="/gis-viewer"
        ></v-list-item>
        -->
      </v-list>
    </v-navigation-drawer>

    <v-main>
      <v-container fluid>
        <RouterView />
      </v-container>
    </v-main>

    <v-footer v-if="!isDataWorkflowRoute" app class="fnp-footer bg-background py-1 px-4">
      <v-row no-gutters align="center">
        <v-col cols="12" sm="6" class="d-flex align-center">
          <img :src="fnpLogo" alt="FNP Logo" class="fnp-logo mr-4" />
          <div class="fnp-info">
            <div class="fnp-tagline">Florida Native Plants</div>
            <div class="text-caption text-grey">
              {{ versionInfo }}
            </div>
          </div>
        </v-col>
        <v-col cols="12" sm="6" class="text-right d-none d-sm-block">
          <div class="text-caption text-grey italic">
            Ecological • Native • Pollinator-Focused
          </div>
        </v-col>
      </v-row>
    </v-footer>

    </template>
  </v-app>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'
import { RouterView, useRouter, useRoute } from 'vue-router'
import { useThemeStore } from '@/stores/theme'
import { useAuthStore } from '@/stores/auth'
const fnpLogo = `${import.meta.env.BASE_URL}assets/fnp-logo.svg`

const drawer = ref(false)
const themeStore = useThemeStore()
const authStore = useAuthStore()
const router = useRouter()
const route = useRoute()
const isDev = import.meta.env.DEV

// Close drawer on route change to avoid double-click issue with temporary drawers
watch(() => route.path, () => {
  drawer.value = false
})

// Routes that should be fullscreen (no app bar, nav, footer)
const fullscreenRoutes = ['interactive-site-plan']
const isFullscreenRoute = computed(() => fullscreenRoutes.includes(route.name as string))
// The data-workflow page is a single-scroll data-entry surface — hide the footer there
// so it doesn't reserve viewport space and shrink the scrollable form area.
const isDataWorkflowRoute = computed(() => route.name === 'data-workflow')

const frontendVersion = ref<string>('')
const backendVersion = ref<string>('')

onMounted(async () => {
  authStore.checkAuth()

  // Fetch frontend version
  fetch('/version.json')
    .then(res => res.json())
    .then(data => {
      const buildDate = new Date(data.buildDate)
      const timeAgo = getTimeAgo(buildDate)
      const shortTime = buildDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      frontendVersion.value = `FE: v${data.version} (${shortTime}, ${timeAgo})`
    })
    .catch(() => {
      frontendVersion.value = 'FE: unknown'
    })

  // Fetch backend version
  fetch('/api/version/')
    .then(res => res.json())
    .then(data => {
      const buildDate = new Date(data.buildDate)
      const timeAgo = getTimeAgo(buildDate)
      const shortTime = buildDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      backendVersion.value = `BE: v${data.version} (${shortTime}, ${timeAgo})`
    })
    .catch(() => {
      backendVersion.value = 'BE: unknown'
    })
})

const handleLogout = async () => {
  await authStore.logout()
  router.push('/login')
}

const versionInfo = computed(() => {
  const parts = [frontendVersion.value, backendVersion.value].filter(v => v)
  return parts.length > 0 ? parts.join(' • ') : 'Loading...'
})

function getTimeAgo(date: Date): string {
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000)

  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}
</script>

<style scoped>
.fnp-footer {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  min-height: 36px;
}

.fnp-logo {
  height: 28px;
  width: auto;
}

.fnp-tagline {
  font-family: "granville", serif;
  font-size: 0.85rem;
  font-weight: 400;
  font-style: normal;
  line-height: 1.1;
}

.fnp-info {
  font-family: "brandon-grotesque", sans-serif;
  font-weight: 400;
  line-height: 1.15;
}
</style>